import { DetailRequest, Status, translateCommonErrorStatus } from "@core/data";
import { Action, createSelector, Selector, State, StateContext, Store } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { ComponentModel, ComponentStateModel } from "./report-component.state-models";
import { ReportComponentService } from "@core/data/api/reports/report-component.service";
import { DeleteReportComponent, EditComponentConfigOptions, EditComponentOptions, InitComponentState, LoadComponentOptions, UpdateReportComponent } from "./report-component.state-actions";
import { tap } from "rxjs/operators";
import { AnyComponentOption, BaseReportComponent, CHART_OPTION_DEFAULT, COMPONENT_CONFIG_DEFAULT, GANTT_OPTION_DEFAULT, GanttComponentData, IMAGE_OPTION_DEFAULT, ImageComponentData, MeasureChartComponentData, ReportComponentOption, ReportComponentType, TEXT_OPTION_DEFAULT, TextComponentData, IndexComponentData } from "@core/data/types/advanced-report";
import { ReportComponentOptionService } from "@core/data/api/reports/report-component-option.service";
import { REPORT_STATE_TOKEN } from "../report-view/report-state/advanced-report.state-models";
import { TableComponentData } from "@core/data/types/Reports/report-tables";
import { RemoveComponentFromPage } from "../report-page/report-page-state/report-page.state-actions";

const DEFAULTS: ComponentStateModel = {
    reportKey: null,
    shareKey: null,
    entities: {}
}

const COMPONENT_DEFAULTS: ComponentModel = {
    key: null,
    pageKey: null,
    component: null,
    compiledData: null,
    status: Status.UNINITIALIZED,
}

@State<ComponentStateModel>({
    name: 'component_state',
    defaults: DEFAULTS,
})
@Injectable()
export class ComponentState {

    @Selector()
    static getEntities(state: ComponentStateModel) {
        return state.entities;
    }

    static getComponent(key: string) {
        return createSelector(
            [ComponentState.getEntities],
            (entities) => entities[key]
        );
    }

    constructor(
        private _store: Store,
        private _componentService: ReportComponentService,
        private _componentOptionService: ReportComponentOptionService,
    ) {}

    @Action(InitComponentState)
    initComponentState(ctx: StateContext<ComponentStateModel>, action: InitComponentState) {
        let state = ctx.getState();

        const detailRequest: DetailRequest = {
            related: [],
        };

        ctx.patchState({
            reportKey: action.reportKey,
            shareKey: action.shareKey,
            entities: {
                ...state.entities,
                [action.key]: {
                    ...COMPONENT_DEFAULTS,
                    key: action.key,
                    status: Status.LOADING,
                }
            }
        });

        return this._componentService.get(action.key, action.shareKey, detailRequest)
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.key]: {
                                    key: action.key,
                                    status: Status.LOADING,
                                    component: result,
                                    compiledData: null,
                                    pageKey: result.pageKey || null,
                                }
                            }
                        });

                        ctx.dispatch(new LoadComponentOptions(action.key));
                    },
                    error => {
                        state = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.key]: {
                                    ...state.entities[action.key],
                                    key: action.key,
                                    component: null,
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            )
    }

    @Action(UpdateReportComponent)
    updateReportComponent(ctx: StateContext<ComponentStateModel>, action: UpdateReportComponent) {
        if (!this.editModeCheck()) return;

        let state = ctx.getState();

        const data = state.entities[action.key];

        const { x, y, cols, rows } = action.item;

        if (!data) return; // TODO HANDLE ERROR

        ctx.patchState({
            entities: {
                ...state.entities,
                [data.key]: {
                    ...data,
                    component: {
                        ...data.component,
                        x: x,
                        y: y,
                        width: cols,
                        height: rows,
                    }
                }
            }
        });

        state = ctx.getState();

        let component: BaseReportComponent = JSON.parse(JSON.stringify(state.entities[action.key].component));
        delete component.options;

        // TODO HANDLE RESULT AND ERROR
        return this._componentService.update(data.key, component)
            .pipe(
                tap(
                    result => {
                        // TODO CHECK RESPONSE STATUS
                    },
                    error => {
                        console.error('error', error)
                    }
                )
            )
    }

    @Action(LoadComponentOptions)
    loadComponentOptions(ctx: StateContext<ComponentStateModel>, action: LoadComponentOptions) {
        let state = ctx.getState()

        // Non-destructive "saving" cue while data (re)compiles — covers the initial load (where
        // status is already LOADING from initComponentState, so this is a no-op) and any later
        // recompile triggered by an edit or a filter change, where the component stays mounted
        // rather than being torn down and rebuilt via the status-driven ngSwitch.
        ctx.patchState({
            entities: {
                ...state.entities,
                [action.componentKey]: {
                    ...state.entities[action.componentKey],
                    saving: true,
                }
            }
        });

        return this._componentOptionService.compile(state.reportKey, action.componentKey, state.shareKey)
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();

                        const model: ComponentModel = state.entities[action.componentKey];
                        const option: AnyComponentOption = result.option;

                        let status: Status = Status.OK;

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.componentKey]: {
                                    ...model,
                                    component: {
                                        ...model.component,
                                        options: {
                                            ...option,
                                            data: {
                                                ...this.fetchOptionDefaults(model.component?.type || ReportComponentType.UNSET),
                                                ...option?.data,
                                            },
                                            config: {
                                                ...COMPONENT_CONFIG_DEFAULT,
                                                ...option?.config,
                                            }
                                        },
                                    } as BaseReportComponent,
                                    compiledData: result.compiled || null,
                                    status: status,
                                    saving: false,
                                }
                            }
                        });
                    },
                    error => {
                        state = ctx.getState();

                        const model: ComponentModel = state.entities[action.componentKey];

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.componentKey]: {
                                    ...model,
                                    component: {
                                        ...model.component,
                                        options: null,
                                    } as BaseReportComponent,
                                    status: translateCommonErrorStatus(error),
                                    saving: false,
                                }
                            }
                        })
                    }
                )
            )
    }

    @Action(EditComponentOptions)
    editComponentOptions(ctx: StateContext<ComponentStateModel>, action: EditComponentOptions) {
        if (!this.editModeCheck()) return;

        let state = ctx.getState();
        let request = null;

        const component: BaseReportComponent = state.entities[action.componentKey].component;
        let defaultData = this.fetchOptionDefaults(component.type);

        let componentConfig = {
            ...COMPONENT_CONFIG_DEFAULT,
            ...component.options?.config,
        };

        if (action.config) {
            componentConfig = {
                ...componentConfig,
                ...action.config,
            }
        }

        const options: ReportComponentOption<TextComponentData | ImageComponentData | MeasureChartComponentData | IndexComponentData | TableComponentData | GanttComponentData> = {
            componentKey: component.key,
            ...component.options,
            data: {
                ...defaultData,
                ...component.options?.data,
                ...action.data,
            },
            config: {
                ...componentConfig
            }
        }

        let model: ComponentModel = state.entities[action.componentKey] || null;

        //? CREATE OPTIONS
        if (!action.key) {
            request = this._componentOptionService.create(options);

            ctx.patchState({
                entities: {
                    ...state.entities,
                    [action.componentKey]: {
                        ...model || COMPONENT_DEFAULTS,
                        status: Status.LOADING,
                    }
                }
            });

        //? UPDATE OPTIONS
        } else {
            request = this._componentOptionService.update(action.key, options)

            // saving (not status) — the component is already loaded and mounted (e.g. a Quill
            // editor mid-edit); flipping status here would tear it down via the outer ngSwitch
            // and drop the user's focus/cursor. LoadComponentOptions clears saving once the
            // recompile that follows a successful save completes.
            ctx.patchState({
                entities: {
                    ...state.entities,
                    [action.componentKey]: {
                        ...model,
                        component: {
                            ...model.component,
                            options: {
                                ...model.component.options,
                                ...options
                            }
                        } as BaseReportComponent,
                        saving: true,
                    }
                }
            })
        }

        return request.pipe(
            tap(
                (result: AnyComponentOption) => {
                    state = ctx.getState();

                    if (!action.key) {
                        model = state.entities[action.componentKey];

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.componentKey]: {
                                    ...model,
                                    component: {
                                        ...model.component,
                                        options: {
                                            ...result
                                        },
                                    } as BaseReportComponent,
                                    status: Status.OK
                                }
                            }
                        });

                    }

                    ctx.dispatch(new LoadComponentOptions(action.componentKey));
                },
                error => {
                    // TODO HANDLE ERROR
                    console.error('error', error)
                }
            )
        )
    }

    @Action(EditComponentConfigOptions)
    editComponentConfigOptions(ctx: StateContext<ComponentStateModel>, action: EditComponentConfigOptions) {
        if (!this.editModeCheck()) return;

        let state = ctx.getState();
        const component: BaseReportComponent = state.entities[action.componentKey].component;
        // returned (not fire-and-forget) so a caller can await the actual save round trip finishing,
        // not just this synchronous re-dispatch
        return ctx.dispatch(new EditComponentOptions(action.componentKey, component.options.data, component.options.key, action.config));
    }

    @Action(DeleteReportComponent)
    deleteReportComponent(ctx: StateContext<ComponentStateModel>, action: DeleteReportComponent) {
        let state: ComponentStateModel = ctx.getState();

        ctx.patchState({
            ...state,
            entities: {
                ...state.entities,
                [action.key]: {
                    ...state.entities[action.key],
                    status: Status.LOADING,
                }
            }
        });

        return this._componentService.delete(action.key, action.showMessage)
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();

                        let dataCopy: ComponentStateModel = JSON.parse(JSON.stringify(state));

                        delete dataCopy.entities[action.key];

                        ctx.dispatch(new RemoveComponentFromPage(action.pageKey, action.key));
                    },
                    error => {
                        ctx.patchState({
                            ...state,
                            entities: {
                                ...state.entities,
                                [action.key]: {
                                    ...state.entities[action.key],
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            )
    }

    private fetchOptionDefaults(componentType: ReportComponentType): TextComponentData | ImageComponentData | MeasureChartComponentData{
        if (componentType === ReportComponentType.TEXT) return TEXT_OPTION_DEFAULT;
        if (componentType === ReportComponentType.IMAGE) return IMAGE_OPTION_DEFAULT;
        if (componentType === ReportComponentType.CHART) return CHART_OPTION_DEFAULT;

        return null
    }

    private editModeCheck() {
        const reportState = this._store.selectSnapshot(REPORT_STATE_TOKEN);

        if (!reportState.editMode) {
            console.warn('[PageState] Action Blocked While In Preview Mode');
            return false
        }

        return true
    }
}